import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';

const AttendanceCalendar = ({ attendanceData = [], month, onMonthChange, onDateSelect, selectedDate }) => {
  const { t } = useTranslation();
  const [internalMonth, setInternalMonth] = useState(new Date());
  const currentMonth = month ?? internalMonth;

  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days?.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1)?.padStart(2, '0')}-${String(day)?.padStart(2, '0')}`;
      const attendance = attendanceData?.find(item => item?.date === dateStr);
      
      days?.push({
        day,
        date: dateStr,
        attendance
      });
    }
    
    return days;
  };

  const formatMonthYear = (date) => {
    return date?.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  };

  const navigateMonth = (direction) => {
    const base = currentMonth ?? new Date();
    const newDate = new Date(base);
    newDate?.setMonth(base?.getMonth() + direction);
    if (onMonthChange) {
      onMonthChange(newDate);
    } else {
      setInternalMonth(newDate);
    }
  };

  const isHollowStatus = (status) => ['leave', 'sick-leave', 'holiday'].includes(status);

  const getStatusCircleClassName = (status) => {
    switch (status) {
      case 'present':
        return 'bg-green-500 text-white';
      case 'absent':
        return 'bg-red-500 text-white';
      case 'half-day':
        return 'bg-orange-500 text-white';
      case 'late':
        return 'bg-yellow-500 text-white';
      case 'early-leave':
        return 'bg-blue-500 text-white';

      // Hollow (outlined) statuses
      case 'leave':
        return 'bg-transparent border-2 border-orange-500 text-orange-600';
      case 'sick-leave':
        return 'bg-transparent border-2 border-red-500 text-red-600';
      case 'holiday':
        return 'bg-transparent border-2 border-purple-500 text-purple-600';

      default:
        return 'bg-gray-200 text-gray-700';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'present': return 'P';
      case 'absent': return 'A';
      case 'leave': return 'L';
      case 'sick-leave': return 'S';
      case 'holiday': return 'H';
      case 'half-day': return 'H';
      case 'late': return 'L';
      case 'early-leave': return 'E';
      default: return '';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'present':
        return 'Present';
      case 'absent':
        return 'Absent';
      case 'leave':
        return 'Leave';
      case 'sick-leave':
        return 'Sick leave';
      case 'holiday':
        return 'Holiday';
      case 'half-day':
        return 'Half Day';
      case 'late':
        return 'Late';
      case 'early-leave':
        return 'Early Clock out';
      default:
        return status || '';
    }
  };

  const getTodayKey = () => new Date()?.toLocaleDateString('en-CA');

  const isToday = (dateStr) => {
    const today = getTodayKey();
    return dateStr === today;
  };

  const isFutureDate = (dateStr) => {
    const today = getTodayKey();
    return dateStr > today;
  };

  const handleDateClick = (date) => {
    if (onDateSelect) {
      onDateSelect(date);
    }
  };

  const days = getDaysInMonth(currentMonth);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="w-full">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigateMonth(-1)}
          className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-colors"
        >
          <Icon name="ChevronLeft" size={16} />
        </button>
        
        <h3 className="text-lg font-semibold text-foreground">
          {formatMonthYear(currentMonth)}
        </h3>
        
        <button
          onClick={() => navigateMonth(1)}
          className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-colors"
        >
          <Icon name="ChevronRight" size={16} />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Week Day Headers */}
        {weekDays?.map((day) => (
          <div key={day} className="p-2 text-center text-xs font-medium text-muted-foreground">
            {day}
          </div>
        ))}

        {/* Calendar Days */}
        {days?.map((dayData, index) => {
          if (!dayData) {
            return <div key={`empty-${index}`} className="p-2 h-12"></div>;
          }

          const { day, date, attendance } = dayData;
          const isCurrentDay = isToday(date);
          const isFuture = isFutureDate(date);
          const isSelected = selectedDate === date;

          return (
            <div
              key={date}
              onClick={() => handleDateClick(date)}
              className={`relative p-2 h-12 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors ${
                isSelected ? 'border-blue-500 border-2' : isCurrentDay ?'ring-2 ring-primary ring-offset-1 border-border/30' : 'border-border/30'
              } ${isFuture ? 'opacity-50' : ''}`}
            >
              <div className="text-xs font-medium text-foreground">{day}</div>
              
              {attendance && (
                <div className="absolute bottom-1 right-1">
                  <div 
                    className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${getStatusCircleClassName(attendance?.status)}`}
                    title={`${getStatusLabel(attendance?.status)} - ${attendance?.checkIn || ''} ${attendance?.checkOut ? `- ${attendance?.checkOut}` : ''}`}
                  >
                    {getStatusText(attendance?.status)}
                  </div>
                </div>
              )}
              
              {!attendance && !isFuture && (
                <div className="absolute bottom-1 right-1">
                  <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="text-xs text-muted-foreground mb-2">{t('Legend')}:</div>
        <div className="flex flex-wrap gap-3 text-xs">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-500 rounded-full"></div>
            <span>{t('Present')}</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-red-500 rounded-full"></div>
            <span>{t('Absent')}</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${getStatusCircleClassName('leave')}`}>
              {getStatusText('leave')}
            </div>
            <span>{t('Leave')}</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${getStatusCircleClassName('sick-leave')}`}>
              {getStatusText('sick-leave')}
            </div>
            <span>{t('Sick Leave')}</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${getStatusCircleClassName('holiday')}`}>
              {getStatusText('holiday')}
            </div>
            <span>{t('Holiday')}</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-orange-500 rounded-full"></div>
            <span>{t('Half Day')}</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
            <span>{t('Late')}</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
            <span>{t('Early Clock out')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceCalendar;